import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Code,
  Divider,
  Flex,
  Center,
  Switch,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Input,
  InputGroup,
  InputLeftAddon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Progress,
  Tooltip,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Collapse,
  useColorModeValue,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { getBackendURL } from "../components/env";
import FunctionDataMonitor from "../components/FunctionDataMonitor";
import DiskIOMonitor from "../components/DiskIOMonitor";

function MonitorPage() {
  const toast = useToast();
  const {
    isOpen: isAlertConfigOpen,
    onOpen: onAlertConfigOpen,
    onClose: closeAlertConfig,
  } = useDisclosure();
  const {
    isOpen: isProcessControlOpen,
    onOpen: onProcessControlOpen,
    onClose: closeProcessControl,
  } = useDisclosure();
  const {
    isOpen: isQueryDetailOpen,
    onOpen: onQueryDetailOpen,
    onClose: closeQueryDetail,
  } = useDisclosure();

  const [selectedQuery, setSelectedQuery] = useState(null);
  const [copiedQuery, setCopiedQuery] = useState(false);

  const handleQueryDetailClose = () => {
    setSelectedQuery(null);
    setCopiedQuery(false);
    closeQueryDetail();
  };

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10); // seconds
  const intervalRef = useRef(null);

  // CPU History for real-time monitoring
  const [cpuHistory, setCpuHistory] = useState([]);
  const maxHistoryPoints = 30;

  // Alert configuration
  const [alertThresholds, setAlertThresholds] = useState({
    cpuWarning: 70,
    cpuCritical: 90,
    slowQueryWarning: 5, // seconds
    slowQueryCritical: 30,
    maxConnections: 150,
    connectionWarning: 80,
    enableAlerts: true,
    enableAutoNotify: false,
    alertEmail: "",
  });
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);

  // Process control
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [processPriority, setProcessPriority] = useState("normal");
  const [isTerminating, setIsTerminating] = useState(false);

  // Optimization recommendations
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  // Background color for alternating rows
  const evenRowBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.100", "gray.600");

  // ── User Activity state ──────────────────────────────────────────────────
  const [userActivityData, setUserActivityData] = useState([]);
  const [displayTimezone, setDisplayTimezone] = useState(
    () => localStorage.getItem('monitorDisplayTimezone') || 'America/New_York'
  );
  const [userActivityLoading, setUserActivityLoading] = useState(false);
  const [userActivityError, setUserActivityError] = useState(null);
  const [metricsHistory, setMetricsHistory] = useState([]); // db_metrics_history snapshots
  const [expandedUserIds, setExpandedUserIds] = useState(new Set());
  const [expandedDays, setExpandedDays] = useState({}); // key: `${userId}-${dateStr}`
  const [userActivityLoaded, setUserActivityLoaded] = useState(false);

  // ── DB Impact classifier ─────────────────────────────────────────────────
  // Returns { level: 'high'|'medium'|'low', reason, color }
  const classifyDbImpact = (event) => {
    const action = (event.action || '').toLowerCase();
    const category = (event.category || '').toLowerCase();
    const page = (
      event.metadata?.page ||
      event.details?.page ||
      event.details?.url ||
      ''
    ).toLowerCase();
    const details = event.details || {};
    const feature = (details.feature || '').toLowerCase();

    // ── Use real DB cost metadata when available ─────────────────────────
    const dbOps = typeof details.db_ops === 'number' ? details.db_ops : null;
    const storageOps = typeof details.storage_ops === 'number'
      ? details.storage_ops
      : typeof details.storage_downloads === 'number'
        ? details.storage_downloads
        : null;
    const totalOps = (dbOps ?? 0) + (storageOps ?? 0);

    if (dbOps !== null || storageOps !== null) {
      // Derive level from actual counts
      if (totalOps >= 5 || storageOps >= 3) {
        const parts = [];
        if (dbOps !== null) parts.push(`${dbOps} DB op${dbOps !== 1 ? 's' : ''}`);
        if (storageOps !== null) parts.push(`${storageOps} storage op${storageOps !== 1 ? 's' : ''}`);
        const fromCache = details.from_cache ? ' (served from cache)' : '';
        return {
          level: 'high', color: 'red',
          reason: `${parts.join(' + ')}${fromCache}`,
          db_ops: dbOps, storage_ops: storageOps,
        };
      }
      if (totalOps >= 1) {
        const parts = [];
        if (dbOps !== null) parts.push(`${dbOps} DB op${dbOps !== 1 ? 's' : ''}`);
        if (storageOps !== null) parts.push(`${storageOps} storage op${storageOps !== 1 ? 's' : ''}`);
        const fromCache = details.from_cache ? ' (served from cache)' : '';
        return {
          level: 'medium', color: 'orange',
          reason: `${parts.join(' + ')}${fromCache}`,
          db_ops: dbOps, storage_ops: storageOps,
        };
      }
      // Zero ops (fully cached)
      return {
        level: 'low', color: 'green',
        reason: `0 DB/storage ops${details.from_cache ? ' — served from cache' : ''}`,
        db_ops: dbOps, storage_ops: storageOps,
      };
    }

    // ── Heuristic fallback for events without cost metadata ──────────────

    // ── HIGH impact ──────────────────────────────────────────────────────
    // Audio recording: continuous backup uploads + DB inserts during session
    if (category === 'audio_recording' && (action === 'started' || action === 'transcribed')) {
      return {
        level: 'high',
        color: 'red',
        reason: action === 'started'
          ? 'Audio recording: continuously uploads backup chunks to storage + inserts DB rows for each checkpoint throughout the session'
          : 'Transcription: large audio download from storage → AI processing → full document save + version creation',
      };
    }
    // Audio recording completed (backup count may be available)
    if (category === 'audio_recording' && action === 'completed' && feature === 'transcription_backups') {
      const count = details.backup_count ?? '?';
      return {
        level: 'high',
        color: 'red',
        reason: `Audio session ended: ${count} backup upload${count !== 1 ? 's' : ''} to storage during recording`,
      };
    }
    // Schedules/notes-c page: SELECT * filtered by date — can be large
    if (page.includes('/schedule') || page.includes('schedule') ||
        details.page?.toLowerCase().includes('schedule')) {
      return {
        level: 'high',
        color: 'red',
        reason: 'Schedules page: SELECT * from notes-c table for the selected date — can return many rows; runs on every page load and date change',
      };
    }
    // Admin page: queries inbox, fax/report queues, user tables
    if (page.includes('/admin') || page.includes('admin') ||
        details.page?.toLowerCase().includes('admin')) {
      return {
        level: 'high',
        color: 'red',
        reason: 'Admin page: queries multiple tables concurrently (inbox, fax queue, reports, user list) — each load triggers several SELECT * operations',
      };
    }
    // Version history modal opened (heuristic — new events have cost metadata above)
    if (feature === 'version_history_modal' || action.includes('version_history') ||
        (action === 'modal_opened' && (details.modal || '').toLowerCase().includes('version'))) {
      return {
        level: 'high',
        color: 'red',
        reason: 'Version history modal: SELECT * from document_backups (all rows for this doc) + downloads N backup files from storage on every open',
      };
    }
    // Forms/reports modal or fax inbox open
    if (action.includes('form_modal') || action.includes('report_modal') ||
        action.includes('fax') || category === 'sms_fax') {
      return {
        level: 'high',
        color: 'red',
        reason: 'Fax/report inbox or forms modal: queries report/forms tables and may trigger Twilio API lookups + DB writes for status updates',
      };
    }

    // ── MEDIUM impact ────────────────────────────────────────────────────
    // Patient page: multi-table join
    if (page.includes('/patient') || details.page?.toLowerCase().includes('patient') ||
        details.patientId || details.patient_id) {
      return {
        level: 'medium',
        color: 'orange',
        reason: 'Patient page: joins patient record + documents + audio recordings tables; also writes activity_history on each visit',
      };
    }
    // Document/editable doc page: schema lookup + version baseline sync (SELECT * + downloads)
    if (page.includes('/document') || page.includes('/doc') ||
        category === 'document' || details.documentId || details.filePath) {
      return {
        level: 'medium',
        color: 'orange',
        reason: 'Document page: loads document + user schema lookup + syncVersionBaselineFromServer (SELECT * backups + downloads up to 5 backup files on first open)',
      };
    }
    // Version created (auto or manual)
    if (action === 'created' && feature === 'version_created') {
      const vType = details.version_type || 'auto';
      const trigger = details.trigger ? ` — ${details.trigger}` : '';
      const vName = details.version_name ? ` "${details.version_name}"` : '';
      return {
        level: 'medium',
        color: 'orange',
        reason: `${vType === 'manual' ? 'Manual' : 'Auto'} version created${vName}${trigger}: uploads delta JSON to storage + INSERT into document_backups table`,
      };
    }
    // Document save
    if (action === 'saved' && category === 'document') {
      return {
        level: 'medium',
        color: 'orange',
        reason: 'Document save: UPDATE document row + optional version creation (storage upload + DB insert) + activity_history write',
      };
    }
    // Login / session start: auth validation + schema lookup + activity write
    if (action === 'login' || action === 'session_start' || action === 'logout') {
      return {
        level: 'medium',
        color: 'orange',
        reason: 'Login/logout: JWT validation + user schema lookup (SELECT from users) + session event write to activity_history',
      };
    }
    // Form submit
    if (category === 'form' && (action === 'submitted' || action === 'draft_saved')) {
      return {
        level: 'medium',
        color: 'orange',
        reason: 'Form submission/save: INSERT or UPDATE in forms table + activity_history write',
      };
    }
    // Recording stopped (saves final backup)
    if (category === 'audio_recording' && action === 'stopped') {
      return {
        level: 'medium',
        color: 'orange',
        reason: 'Recording stopped: saves final backup to storage + inserts final backup row + may trigger document creation',
      };
    }

    // ── LOW impact ───────────────────────────────────────────────────────
    return {
      level: 'low',
      color: 'green',
      reason: 'Standard tracked action: writes a single row update to activity_history in the users table',
    };
  };

  const fetchUserActivity = async () => {
    setUserActivityLoading(true);
    setUserActivityError(null);
    try {
      const backendUrl = getBackendURL();
      const res = await fetch(`${backendUrl}/api/all-users-activity/`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const users = json.users || [];
      setUserActivityData(users);
      setUserActivityLoaded(true);

      // Fetch metrics history covering the range of all visible events (±1 hour buffer)
      const allTs = users.flatMap(u => (u.events || []).map(e => e.timestamp)).filter(Boolean);
      if (allTs.length > 0) {
        const minTs = new Date(Math.min(...allTs.map(t => new Date(t).getTime())) - 3600_000).toISOString();
        const maxTs = new Date(Math.max(...allTs.map(t => new Date(t).getTime())) + 3600_000).toISOString();
        try {
          const mRes = await fetch(`${backendUrl}/api/monitor/metrics-history/?from=${encodeURIComponent(minTs)}&to=${encodeURIComponent(maxTs)}`);
          if (mRes.ok) {
            const mJson = await mRes.json();
            setMetricsHistory(mJson.metrics || []);
          }
        } catch { /* metrics history is optional — silently ignore */ }
      }
    } catch (err) {
      setUserActivityError(err.message || 'Failed to load user activity');
    } finally {
      setUserActivityLoading(false);
    }
  };

  /** Find the nearest db_metrics_history snapshot to a given ISO timestamp. */
  const getNearestMetrics = (ts) => {
    if (!metricsHistory.length || !ts) return null;
    const target = new Date(ts).getTime();
    let best = null, bestDiff = Infinity;
    for (const m of metricsHistory) {
      const diff = Math.abs(new Date(m.sampled_at).getTime() - target);
      if (diff < bestDiff) { bestDiff = diff; best = m; }
    }
    // Only show if the snapshot is within 5 minutes of the event
    return bestDiff <= 5 * 60 * 1000 ? best : null;
  };

  const toggleUserExpand = (userId) => {
    setExpandedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleDayExpand = (userId, dateStr) => {
    const key = `${userId}-${dateStr}`;
    setExpandedDays(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Copy all rows for a user to clipboard as plain text
  const copyUserRows = (user) => {
    const lines = [
      `User: ${user.name} <${user.email}> (${user.role})`,
      `Events: ${user.event_count} · ~${
        user.events.map(ev => classifyDbImpact(ev)).reduce((sum, imp) => {
          const actual = (imp.db_ops ?? 0) + (imp.storage_ops ?? 0);
          if (actual > 0) return sum + actual;
          if (imp.level === 'high') return sum + 4;
          if (imp.level === 'medium') return sum + 1;
          return sum;
        }, 0)
      } ops`,
      '',
    ];
    for (const ev of user.events) {
      const ts = ev.timestamp
        ? new Date(ev.timestamp).toLocaleString('en-US', { timeZone: displayTimezone })
        : '—';
      const impact = classifyDbImpact(ev);
      const detail = [
        ev.details?.patientName && `Patient: ${ev.details.patientName}`,
        ev.details?.documentName && `Doc: ${ev.details.documentName}`,
        ev.details?.version_name && `Version: ${ev.details.version_name}`,
        ev.details?.trigger && `Trigger: ${ev.details.trigger}`,
        ev.details?.filePath && `Path: ${ev.details.filePath.split('/').pop()}`,
      ].filter(Boolean).join(' · ') || ev.details?.page || ev.metadata?.page || '';
      const count = ev.total_count > 1 ? ` ×${ev.total_count}` : '';
      lines.push(
        `[${ts}] [${impact.level.toUpperCase()}] ${ev.action || '—'}${count} · ${ev.category || '—'}${detail ? ' · ' + detail : ''}`
      );
    }
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      toast({ title: `Copied ${user.name}'s activity`, status: 'success', duration: 2000, isClosable: true });
    }).catch(() => {
      toast({ title: 'Copy failed', status: 'error', duration: 2000, isClosable: true });
    });
  };

  // Group a user's events by local date string (YYYY-MM-DD)
  const groupEventsByDay = (events) => {
    const groups = {};
    for (const ev of events) {
      if (!ev.timestamp) continue;
      let dateStr;
      try {
        dateStr = new Date(ev.timestamp).toLocaleDateString('en-CA', { timeZone: displayTimezone });
      } catch {
        dateStr = ev.timestamp.slice(0, 10);
      }
      if (!groups[dateStr]) groups[dateStr] = [];
      groups[dateStr].push(ev);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0])); // newest first
  };

  const formatTs = (ts) => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        timeZone: displayTimezone,
      });
    }
    catch { return ts; }
  };

  const formatDateLabel = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T12:00:00');
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      if (d.toDateString() === today.toDateString()) return `Today · ${dateStr}`;
      if (d.toDateString() === yesterday.toDateString()) return `Yesterday · ${dateStr}`;
      return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  // Count high-impact events for a user
  const highImpactCount = (user) =>
    user.events.filter(ev => classifyDbImpact(ev).level === 'high').length;

  const fetchMonitorData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/database-monitor/`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("[MonitorPage] Full API response:", result);
      console.log("[MonitorPage] connections:", result?.connections);
      console.log(
        "[MonitorPage] connections keys:",
        result?.connections ? Object.keys(result.connections) : "N/A",
      );
      setData(result);
      setLastUpdated(new Date());

      // Update CPU history for real-time monitoring
      if (result?.supabase_cpu?.cpu_percent) {
        updateCpuHistory(result.supabase_cpu.cpu_percent);
      }

      // Check for alerts
      checkAlerts(result);

      // Update recommendations
      setRecommendations(generateRecommendations(result));
    } catch (err) {
      console.error("Error fetching monitor data:", err);
      setError(err.message || "Failed to fetch database monitor data");
      toast({
        title: "Error",
        description: err.message || "Failed to fetch database monitor data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitorData();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchMonitorData();
      }, refreshInterval * 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval]);

  const formatDate = (date) => {
    if (!date) return "Never";
    return date.toLocaleTimeString();
  };

  const getStateColor = (state) => {
    switch (state) {
      case "active":
        return "green";
      case "idle in transaction":
        return "orange";
      case "idle in transaction (aborted)":
        return "red";
      default:
        return "gray";
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "N/A";
    return num.toLocaleString();
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let unitIndex = 0;
    let value = bytes;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    return `${value.toFixed(2)} ${units[unitIndex]}`;
  };

  // Helper to get connection data with multiple fallbacks
  const getConn = (key, fallbackKey) => {
    const conn = data?.connections || {};
    // Try exact key first, then fallback, then try lowercase, then try without underscore
    const value =
      conn[key] ??
      conn[fallbackKey] ??
      conn[key.toLowerCase()] ??
      conn[fallbackKey?.toLowerCase()] ??
      null;
    console.log(`[MonitorPage] getConn(${key}, ${fallbackKey}):`, value);
    return value;
  };

  // Update CPU history and check for alerts
  const updateCpuHistory = (cpuPercent) => {
    const timestamp = new Date();
    setCpuHistory((prev) => {
      const newHistory = [...prev, { time: timestamp, value: cpuPercent }];
      if (newHistory.length > maxHistoryPoints) {
        return newHistory.slice(-maxHistoryPoints);
      }
      return newHistory;
    });
  };

  // Check alert thresholds and trigger alerts
  const checkAlerts = (currentData) => {
    if (!alertThresholds.enableAlerts || !currentData) return;

    const newAlerts = [];
    const timestamp = new Date();

    // CPU alerts
    const cpuPercent = currentData.supabase_cpu?.cpu_percent || 0;
    if (cpuPercent >= alertThresholds.cpuCritical) {
      newAlerts.push({
        id: `cpu-critical-${Date.now()}`,
        type: "critical",
        title: "Critical CPU Usage",
        message: `CPU usage is at ${cpuPercent.toFixed(1)}% - exceeds critical threshold of ${alertThresholds.cpuCritical}%`,
        timestamp: timestamp,
        metric: "cpu",
        value: cpuPercent,
      });
    } else if (cpuPercent >= alertThresholds.cpuWarning) {
      newAlerts.push({
        id: `cpu-warning-${Date.now()}`,
        type: "warning",
        title: "High CPU Usage",
        message: `CPU usage is at ${cpuPercent.toFixed(1)}% - exceeds warning threshold of ${alertThresholds.cpuWarning}%`,
        timestamp: timestamp,
        metric: "cpu",
        value: cpuPercent,
      });
    }

    // Connection alerts
    const activeConns =
      currentData.connections?.numbackends ||
      currentData.connections?.num_backends ||
      0;
    if (activeConns >= alertThresholds.maxConnections) {
      newAlerts.push({
        id: `conn-critical-${Date.now()}`,
        type: "critical",
        title: "Max Connections Reached",
        message: `Active connections (${activeConns}) have reached the maximum limit (${alertThresholds.maxConnections})`,
        timestamp: timestamp,
        metric: "connections",
        value: activeConns,
      });
    } else if (activeConns >= alertThresholds.connectionWarning) {
      newAlerts.push({
        id: `conn-warning-${Date.now()}`,
        type: "warning",
        title: "High Connection Count",
        message: `Active connections (${activeConns}) are above warning threshold (${alertThresholds.connectionWarning})`,
        timestamp: timestamp,
        metric: "connections",
        value: activeConns,
      });
    }

    // Slow query alerts
    const slowQueries = currentData.slow_queries || [];
    const criticalSlowQueries = slowQueries.filter(
      (q) => q.duration_seconds >= alertThresholds.slowQueryCritical,
    );
    const warningSlowQueries = slowQueries.filter(
      (q) =>
        q.duration_seconds >= alertThresholds.slowQueryWarning &&
        q.duration_seconds < alertThresholds.slowQueryCritical,
    );

    if (criticalSlowQueries.length > 0) {
      newAlerts.push({
        id: `slow-critical-${Date.now()}`,
        type: "critical",
        title: "Critical Slow Queries",
        message: `${criticalSlowQueries.length} queries are running longer than ${alertThresholds.slowQueryCritical}s`,
        timestamp: timestamp,
        metric: "slow_queries",
        value: criticalSlowQueries.length,
      });
    }

    if (warningSlowQueries.length > 0) {
      newAlerts.push({
        id: `slow-warning-${Date.now()}`,
        type: "warning",
        title: "Slow Queries Detected",
        message: `${warningSlowQueries.length} queries are running longer than ${alertThresholds.slowQueryWarning}s`,
        timestamp: timestamp,
        metric: "slow_queries",
        value: warningSlowQueries.length,
      });
    }

    // Update active alerts (remove duplicates)
    if (newAlerts.length > 0) {
      setActiveAlerts((prev) => {
        const existingIds = prev.map((a) =>
          a.id.split("-").slice(0, 2).join("-"),
        );
        const filtered = newAlerts.filter(
          (a) => !existingIds.includes(a.id.split("-").slice(0, 2).join("-")),
        );
        return [...prev, ...filtered].slice(-10); // Keep last 10 alerts
      });

      // Add to alert history
      setAlertHistory((prev) => [...newAlerts, ...prev].slice(-50)); // Keep last 50 alerts

      // Show toast notification
      newAlerts.forEach((alert) => {
        toast({
          title: alert.title,
          description: alert.message,
          status: alert.type === "critical" ? "error" : "warning",
          duration: 8000,
          isClosable: true,
          position: "top-right",
        });
      });
    }
  };

  // Generate optimization recommendations
  const generateRecommendations = (currentData) => {
    const recs = [];

    if (!currentData) return recs;

    // Check slow queries
    const slowQueries = currentData.slow_queries || [];
    if (slowQueries.length > 0) {
      recs.push({
        priority: "high",
        category: "slow_queries",
        title: "Optimize Slow Queries",
        description: `${slowQueries.length} slow queries detected. Consider adding indexes or optimizing query plans.`,
        action:
          "Review and optimize slow queries in the Active Queries section",
      });
    }

    // Check cache hit ratio
    const conn = currentData.connections || {};
    const blksRead = conn.blks_read || 0;
    const blksHit = conn.blks_hit || 0;
    const cacheRatio =
      blksRead > 0 ? (blksHit / (blksRead + blksHit)) * 100 : 100;
    if (cacheRatio < 90) {
      recs.push({
        priority: "medium",
        category: "cache",
        title: "Improve Cache Hit Ratio",
        description: `Cache hit ratio is at ${cacheRatio.toFixed(1)}%. Consider increasing cache size or optimizing frequent queries.`,
        action: "Review tables with low cache hit ratio",
      });
    }

    // Check for unindexed tables
    const unindexed = currentData.unindexed_tables || [];
    if (unindexed.length > 0) {
      recs.push({
        priority: "medium",
        category: "indexing",
        title: "Add Missing Indexes",
        description: `${unindexed.length} tables are using sequential scans only. Adding indexes can significantly improve performance.`,
        action: `Tables requiring indexes: ${unindexed
          .slice(0, 3)
          .map((t) => t.relname)
          .join(", ")}`,
      });
    }

    // Check for unused indexes
    const indexUsage = currentData.index_usage || [];
    const unusedIndexes = indexUsage.filter((i) => i.idx_scan === 0);
    if (unusedIndexes.length > 5) {
      recs.push({
        priority: "low",
        category: "indexing",
        title: "Remove Unused Indexes",
        description: `${unusedIndexes.length} indexes have never been scanned. Removing them can reduce overhead.`,
        action:
          "Consider dropping unused indexes to reduce storage and maintenance overhead",
      });
    }

    // Check for bloat
    const vacuumStats = currentData.vacuum_stats || [];
    if (vacuumStats.length > 0) {
      recs.push({
        priority: "medium",
        category: "maintenance",
        title: "Vacuum Tables",
        description: `${vacuumStats.length} tables have dead tuples. Running VACUUM can reclaim space and improve performance.`,
        action: `Tables needing vacuum: ${vacuumStats
          .slice(0, 3)
          .map((t) => t.relname)
          .join(", ")}`,
      });
    }

    // Check temp files
    const tempFiles = conn.temp_files || 0;
    if (tempFiles > 100) {
      recs.push({
        priority: "low",
        category: "memory",
        title: "Reduce Temp File Usage",
        description: `${tempFiles} temp files created. This indicates memory pressure or large sorts.`,
        action: "Consider increasing work_mem or optimizing large sorts",
      });
    }

    // Check for idle connections
    if (slowQueries.length === 0 && (conn.numbackends || 0) > 20) {
      recs.push({
        priority: "low",
        category: "connections",
        title: "Review Connection Pool",
        description: `High number of idle connections (${conn.numbackends || 0}). Consider connection pooling optimization.`,
        action: "Review and close idle connections in application",
      });
    }

    return recs;
  };

  // Terminate a process (simulated - actual implementation would call API)
  const handleTerminateProcess = async (pid) => {
    setIsTerminating(true);
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/terminate-process/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pid }),
      });

      if (!response.ok) {
        throw new Error(`Failed to terminate process: ${response.status}`);
      }

      toast({
        title: "Process Terminated",
        description: `Process ${pid} has been terminated`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh data after termination
      fetchMonitorData();
    } catch (err) {
      toast({
        title: "Error",
        description: `Failed to terminate process: ${err.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTerminating(false);
      closeProcessControl();
    }
  };

  // Set process priority (simulated)
  const handleSetPriority = async (pid, priority) => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/set-priority/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pid, priority }),
      });

      if (!response.ok) {
        throw new Error(`Failed to set priority: ${response.status}`);
      }

      toast({
        title: "Priority Updated",
        description: `Process ${pid} priority set to ${priority}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setProcessPriority(priority);
    } catch (err) {
      toast({
        title: "Error",
        description: `Failed to set priority: ${err.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Clear alerts
  const clearAlerts = () => {
    setActiveAlerts([]);
    toast({
      title: "Alerts Cleared",
      status: "info",
      duration: 2000,
    });
  };

  if (isLoading && !data) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading database monitor data...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header with enhanced controls */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg">System Monitor</Heading>
            <Text color="gray.600">
              Real-time CPU, database performance and resource monitoring
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button
              onClick={fetchMonitorData}
              isLoading={isLoading}
              colorScheme="blue"
              variant="outline"
            >
              Refresh
            </Button>
            <Button
              onClick={() => setAutoRefresh(!autoRefresh)}
              colorScheme={autoRefresh ? "green" : "gray"}
              variant={autoRefresh ? "solid" : "outline"}
            >
              {autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
            </Button>
            <Button
              onClick={onAlertConfigOpen}
              colorScheme="orange"
              variant="outline"
              leftIcon={<Text>⚙</Text>}
            >
              Alert Config
            </Button>
            <Button
              onClick={() => setShowRecommendations(!showRecommendations)}
              colorScheme={recommendations.length > 0 ? "purple" : "gray"}
              variant={showRecommendations ? "solid" : "outline"}
            >
              {recommendations.length > 0
                ? `${recommendations.length} Recommendations`
                : "Recommendations"}
            </Button>
          </HStack>
        </Flex>

        {/* Active Alerts Panel */}
        {activeAlerts.length > 0 && (
          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <HStack>
                <AlertIcon />
                <Text fontWeight="bold">
                  Active Alerts ({activeAlerts.length})
                </Text>
              </HStack>
              <Button size="sm" variant="ghost" onClick={clearAlerts}>
                Clear All
              </Button>
            </Flex>
            <VStack spacing={2} align="stretch">
              {activeAlerts.slice(0, 5).map((alert) => (
                <Alert
                  key={alert.id}
                  status={alert.type === "critical" ? "error" : "warning"}
                  borderRadius="md"
                >
                  <Box flex="1">
                    <AlertTitle>{alert.title}</AlertTitle>
                    <AlertDescription fontSize="sm">
                      {alert.message}
                    </AlertDescription>
                  </Box>
                  <Text fontSize="xs" color="gray.500">
                    {alert.timestamp.toLocaleTimeString()}
                  </Text>
                </Alert>
              ))}
            </VStack>
          </Box>
        )}

        {/* Auto-refresh interval control */}
        {autoRefresh && (
          <Box p={3} bg="blue.50" borderRadius="md">
            <Flex justify="space-between" align="center">
              <Text fontSize="sm" fontWeight="medium">
                Refresh Interval: {refreshInterval} seconds
              </Text>
              <HStack spacing={2}>
                <Button
                  size="xs"
                  onClick={() => setRefreshInterval(5)}
                  variant={refreshInterval === 5 ? "solid" : "outline"}
                >
                  5s
                </Button>
                <Button
                  size="xs"
                  onClick={() => setRefreshInterval(10)}
                  variant={refreshInterval === 10 ? "solid" : "outline"}
                >
                  10s
                </Button>
                <Button
                  size="xs"
                  onClick={() => setRefreshInterval(30)}
                  variant={refreshInterval === 30 ? "solid" : "outline"}
                >
                  30s
                </Button>
                <Button
                  size="xs"
                  onClick={() => setRefreshInterval(60)}
                  variant={refreshInterval === 60 ? "solid" : "outline"}
                >
                  1m
                </Button>
              </HStack>
            </Flex>
          </Box>
        )}

        {/* CPU History Chart (Real-time) */}
        {cpuHistory.length > 1 && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">Real-time CPU Usage History</Heading>
                <Badge
                  colorScheme={
                    data?.supabase_cpu?.cpu_percent > 70 ? "orange" : "green"
                  }
                >
                  Current: {data?.supabase_cpu?.cpu_percent?.toFixed(1) || 0}%
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <Box h="120px" position="relative">
                {/* Simple ASCII-style chart */}
                <Flex h="100%" align="flex-end" gap={1}>
                  {cpuHistory.map((point, idx) => {
                    const height = Math.max(5, (point.value / 100) * 100);
                    const color =
                      point.value >= alertThresholds.cpuCritical
                        ? "red.500"
                        : point.value >= alertThresholds.cpuWarning
                          ? "orange.400"
                          : "green.400";
                    return (
                      <Tooltip
                        key={idx}
                        label={`${point.value.toFixed(1)}% at ${point.time.toLocaleTimeString()}`}
                      >
                        <Box
                          flex="1"
                          bg={color}
                          borderRadius="sm"
                          h={`${height}%`}
                          minW="4px"
                          transition="all 0.3s"
                        />
                      </Tooltip>
                    );
                  })}
                </Flex>
                {/* Threshold lines */}
                <Box
                  position="absolute"
                  bottom={`${(alertThresholds.cpuWarning / 100) * 100}%`}
                  left={0}
                  right={0}
                  borderTop="1px dashed"
                  borderColor="orange.400"
                  opacity={0.5}
                >
                  <Text
                    fontSize="xs"
                    color="orange.400"
                    position="absolute"
                    right={0}
                    top={-3}
                  >
                    Warning: {alertThresholds.cpuWarning}%
                  </Text>
                </Box>
                <Box
                  position="absolute"
                  bottom={`${(alertThresholds.cpuCritical / 100) * 100}%`}
                  left={0}
                  right={0}
                  borderTop="1px dashed"
                  borderColor="red.400"
                  opacity={0.5}
                >
                  <Text
                    fontSize="xs"
                    color="red.400"
                    position="absolute"
                    right={0}
                    top={-3}
                  >
                    Critical: {alertThresholds.cpuCritical}%
                  </Text>
                </Box>
              </Box>
              <Flex justify="space-between" mt={2}>
                <Text fontSize="xs" color="gray.500">
                  Last {cpuHistory.length} readings over{" "}
                  {cpuHistory.length * refreshInterval} seconds
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Avg:{" "}
                  {(
                    cpuHistory.reduce((sum, p) => sum + p.value, 0) /
                    cpuHistory.length
                  ).toFixed(1)}
                  %
                </Text>
              </Flex>
            </CardBody>
          </Card>
        )}

        {/* Error */}
        {error && (
          <Box
            p={4}
            bg="red.50"
            borderRadius="md"
            border="1px"
            borderColor="red.200"
          >
            <Text color="red.600">{error}</Text>
          </Box>
        )}

        {/* Recommendations Panel */}
        <Collapse in={showRecommendations}>
          <Card borderColor="purple.300" borderWidth="2px">
            <CardHeader bg="purple.50">
              <HStack justify="space-between">
                <Heading size="md">⚡ Optimization Recommendations</Heading>
                <Badge colorScheme="purple">
                  {recommendations.length} items
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              {recommendations.length > 0 ? (
                <VStack spacing={3} align="stretch">
                  {recommendations.map((rec, idx) => (
                    <Box
                      key={idx}
                      p={3}
                      borderRadius="md"
                      bg={
                        rec.priority === "high"
                          ? "red.50"
                          : rec.priority === "medium"
                            ? "orange.50"
                            : "blue.50"
                      }
                      borderLeft="4px solid"
                      borderLeftColor={
                        rec.priority === "high"
                          ? "red.500"
                          : rec.priority === "medium"
                            ? "orange.500"
                            : "blue.500"
                      }
                    >
                      <Flex justify="space-between" align="start">
                        <Box flex="1">
                          <HStack mb={1}>
                            <Badge
                              colorScheme={
                                rec.priority === "high"
                                  ? "red"
                                  : rec.priority === "medium"
                                    ? "orange"
                                    : "blue"
                              }
                              size="sm"
                            >
                              {rec.priority.toUpperCase()}
                            </Badge>
                            <Text fontWeight="bold">{rec.title}</Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            {rec.description}
                          </Text>
                          <Text fontSize="sm" color="purple.600" mt={1}>
                            → {rec.action}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text color="green.500">
                  No optimization recommendations at this time. System is
                  performing well!
                </Text>
              )}
            </CardBody>
          </Card>
        </Collapse>

        {/* Connection Stats */}
        {data?.connections && (
          <Card>
            <CardHeader>
              <Heading size="md">Database Performance & Resources</Heading>
            </CardHeader>
            <CardBody>
              {/* CPU Percentage Display */}
              {data?.supabase_cpu && (
                <Box
                  mb={4}
                  p={4}
                  bg={
                    data.supabase_cpu.cpu_percent > 70
                      ? "orange.50"
                      : "green.50"
                  }
                  borderRadius="md"
                >
                  <HStack justify="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="bold">
                        Supabase CPU Usage
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {data.supabase_cpu.method === "approximation"
                          ? "Approximated from connections"
                          : "Real-time from Supabase API"}
                      </Text>
                    </VStack>
                    <Text
                      fontSize="3xl"
                      fontWeight="bold"
                      color={
                        data.supabase_cpu.cpu_percent > 70
                          ? "orange.500"
                          : "green.500"
                      }
                    >
                      {data.supabase_cpu.cpu_percent?.toFixed(1) || 0}%
                    </Text>
                  </HStack>
                  {data.supabase_cpu.active_connections && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Active connections: {data.supabase_cpu.active_connections}
                    </Text>
                  )}
                </Box>
              )}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                <Stat>
                  <StatLabel>Active Backends</StatLabel>
                  <StatNumber>
                    {formatNumber(getConn("numbackends", "numbackends"))}
                  </StatNumber>
                  <StatHelpText>Current connections</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Transactions Committed</StatLabel>
                  <StatNumber>
                    {formatNumber(getConn("xact_commit", "xact_commit"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Transactions Rolled Back</StatLabel>
                  <StatNumber
                    color={
                      getConn("xact_rollback", "xact_rollback") > 0
                        ? "orange"
                        : "inherit"
                    }
                  >
                    {formatNumber(getConn("xact_rollback", "xact_rollback"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Cache Hit Ratio</StatLabel>
                  <StatNumber>
                    {getConn("blks_read", "blks_read") > 0
                      ? (
                          (getConn("blks_hit", "blks_hit") /
                            (getConn("blks_read", "blks_read") +
                              getConn("blks_hit", "blks_hit"))) *
                          100
                        ).toFixed(2)
                      : 100.0}
                    %
                  </StatNumber>
                  <StatHelpText>
                    {formatNumber(getConn("blks_hit", "blks_hit"))} hits /{" "}
                    {formatNumber(getConn("blks_read", "blks_read"))} reads
                  </StatHelpText>
                </Stat>
              </SimpleGrid>

              <Divider my={4} />

              <Heading size="sm" mb={3}>
                CPU & Resource Usage
              </Heading>
              <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4}>
                <Stat>
                  <StatLabel>Tuples Returned</StatLabel>
                  <StatNumber fontSize="md">
                    {formatNumber(getConn("tup_returned", "tupreturned"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Tuples Fetched</StatLabel>
                  <StatNumber fontSize="md">
                    {formatNumber(getConn("tup_fetched", "tupfetched"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Rows Inserted</StatLabel>
                  <StatNumber fontSize="md" color="green.500">
                    {formatNumber(getConn("tup_inserted", "tupinserted"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Rows Updated</StatLabel>
                  <StatNumber fontSize="md" color="blue.500">
                    {formatNumber(getConn("tup_updated", "tupupdated"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Rows Deleted</StatLabel>
                  <StatNumber fontSize="md" color="red.500">
                    {formatNumber(getConn("tup_deleted", "tupdeleted"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Deadlocks</StatLabel>
                  <StatNumber
                    fontSize="md"
                    color={
                      getConn("deadlocks", "deadlocks") > 0
                        ? "red.500"
                        : "inherit"
                    }
                  >
                    {formatNumber(getConn("deadlocks", "deadlocks"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Conflicts</StatLabel>
                  <StatNumber
                    fontSize="md"
                    color={
                      getConn("conflicts", "conflicts") > 0
                        ? "orange"
                        : "inherit"
                    }
                  >
                    {formatNumber(getConn("conflicts", "conflicts"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Temp Files</StatLabel>
                  <StatNumber
                    fontSize="md"
                    color={
                      getConn("temp_files", "tempfiles") > 0
                        ? "orange"
                        : "inherit"
                    }
                  >
                    {formatNumber(getConn("temp_files", "tempfiles"))}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Temp Bytes</StatLabel>
                  <StatNumber fontSize="md">
                    {formatBytes(getConn("temp_bytes", "tempbytes"))}
                  </StatNumber>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* Active Queries with Resource Usage */}
        <Card>
          <CardHeader>
            <HStack justify="space-between">
              <HStack>
                <Heading size="md">Active Queries</Heading>
                <Badge
                  colorScheme={
                    data?.active_queries?.length > 0 ? "orange" : "green"
                  }
                  fontSize="md"
                >
                  {data?.active_queries?.length || 0} active
                </Badge>
              </HStack>
              <HStack>
                <Tooltip label="Terminate high-CPU query">
                  <IconButton
                    icon={<Text>⏹</Text>}
                    colorScheme="red"
                    variant="outline"
                    size="sm"
                    isDisabled={!data?.active_queries?.length}
                    onClick={() => {
                      if (data?.active_queries?.length > 0) {
                        setSelectedProcess(data.active_queries[0]);
                        onProcessControlOpen();
                      }
                    }}
                  />
                </Tooltip>
              </HStack>
            </HStack>
          </CardHeader>
          <CardBody>
            {data?.active_queries?.length > 0 ? (
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>PID</Th>
                      <Th>User</Th>
                      <Th>State</Th>
                      <Th>Wait Event</Th>
                      <Th>IO Read</Th>
                      <Th>Temp MB</Th>
                      <Th>Query Type</Th>
                      <Th>Duration</Th>
                      <Th>Query</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.active_queries.map((query, idx) => {
                      const isSlow =
                        query.duration_seconds >=
                        alertThresholds.slowQueryWarning;
                      const isHighCpu =
                        query.duration_seconds >=
                        alertThresholds.slowQueryCritical;
                      const isBlocked =
                        query.blocking_pid > 0 ||
                        query.blocked_by === "blocked";
                      // Calculate temp MB from temp_bytes
                      const tempMB = (query.temp_bytes || 0) / (1024 * 1024);
                      // Calculate IO from blocks_read
                      const ioRead = ((query.blocks_read || 0) * 8.192) / 1024; // Convert blocks to MB
                      return (
                        <Tr
                          key={idx}
                          bg={
                            isHighCpu
                              ? "red.50"
                              : isBlocked
                                ? "purple.50"
                                : isSlow
                                  ? "orange.50"
                                  : "inherit"
                          }
                        >
                          <Td>
                            <Code fontSize="xs">{query.pid}</Code>
                            {isBlocked && (
                              <Badge ml={1} colorScheme="purple" size="xs">
                                BLOCKED
                              </Badge>
                            )}
                          </Td>
                          <Td>
                            <Text fontSize="sm">{query.usename}</Text>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStateColor(query.state)}>
                              {query.state}
                            </Badge>
                          </Td>
                          <Td>
                            <Tooltip label={query.wait_event_type || "CPU"}>
                              <Badge
                                colorScheme={
                                  query.wait_event_type === "Lock"
                                    ? "red"
                                    : query.wait_event_type === "IO"
                                      ? "orange"
                                      : query.wait_event_type === "Buffer"
                                        ? "purple"
                                        : "gray"
                                }
                                size="sm"
                              >
                                {query.wait_event_type || "CPU"}
                              </Badge>
                            </Tooltip>
                          </Td>
                          <Td>
                            <Tooltip label={`${query.blocks_read || 0} blocks`}>
                              <Text
                                fontSize="sm"
                                color={ioRead > 10 ? "orange.500" : "gray.500"}
                              >
                                {ioRead < 0.01 ? "< 0.01" : ioRead.toFixed(2)}{" "}
                                MB
                              </Text>
                            </Tooltip>
                          </Td>
                          <Td>
                            <Tooltip label={`${query.temp_bytes || 0} bytes`}>
                              <Text
                                fontSize="sm"
                                color={tempMB > 1 ? "purple.500" : "gray.500"}
                              >
                                {tempMB < 0.001 ? "< 0.001" : tempMB.toFixed(3)}{" "}
                                MB
                              </Text>
                            </Tooltip>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={
                                query.query_type === "SELECT"
                                  ? "blue"
                                  : query.query_type === "INSERT"
                                    ? "green"
                                    : query.query_type === "UPDATE"
                                      ? "orange"
                                      : query.query_type === "DELETE"
                                        ? "red"
                                        : query.query_type === "VACUUM"
                                          ? "purple"
                                          : "gray"
                              }
                              size="sm"
                            >
                              {query.query_type || "OTHER"}
                            </Badge>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Text
                                fontSize="sm"
                                color={
                                  isHighCpu
                                    ? "red.500"
                                    : isSlow
                                      ? "orange.500"
                                      : "inherit"
                                }
                                fontWeight={isSlow ? "bold" : "normal"}
                              >
                                {query.duration_seconds || 0}s
                              </Text>
                              {isHighCpu && (
                                <Badge colorScheme="red" size="xs">
                                  CRIT
                                </Badge>
                              )}
                              {isSlow && !isHighCpu && (
                                <Badge colorScheme="orange" size="xs">
                                  SLOW
                                </Badge>
                              )}
                            </HStack>
                          </Td>
                          <Td maxW="200px">
                            <Box
                              onClick={() => {
                                setSelectedQuery(query);
                                onQueryDetailOpen();
                              }}
                              cursor="pointer"
                              _hover={{ bg: "gray.100" }}
                              borderRadius="md"
                              p={1}
                            >
                              <Code
                                fontSize="xs"
                                display="block"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                              >
                                {query.query || "-"}
                              </Code>
                            </Box>
                          </Td>
                          <Td>
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<Text>⋮</Text>}
                                variant="ghost"
                                size="sm"
                              />
                              <MenuList>
                                <MenuItem
                                  icon={<Text>⏹</Text>}
                                  color="red.500"
                                  onClick={() => {
                                    setSelectedProcess(query);
                                    onProcessControlOpen();
                                  }}
                                >
                                  Terminate
                                </MenuItem>
                                <MenuItem
                                  icon={<Text>↑</Text>}
                                  onClick={() =>
                                    handleSetPriority(query.pid, "high")
                                  }
                                >
                                  Set High Priority
                                </MenuItem>
                                <MenuItem
                                  icon={<Text>↓</Text>}
                                  onClick={() =>
                                    handleSetPriority(query.pid, "low")
                                  }
                                >
                                  Set Low Priority
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text color="gray.500">No active queries at this time.</Text>
            )}
          </CardBody>
        </Card>

        {/* ── User Activity Log ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Box>
              <Heading size="md">👤 User Activity Log</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Per-user DB operations grouped by day — with estimated CPU / Disk I/O impact
              </Text>
            </Box>
            <HStack spacing={2}>
              <Select
                size="sm"
                value={displayTimezone}
                onChange={e => {
                  setDisplayTimezone(e.target.value);
                  localStorage.setItem('monitorDisplayTimezone', e.target.value);
                }}
                w="200px"
              >
                <option value="America/New_York">Eastern (ET)</option>
                <option value="America/Chicago">Central (CT)</option>
                <option value="America/Denver">Mountain (MT)</option>
                <option value="America/Los_Angeles">Pacific (PT)</option>
                <option value="UTC">UTC</option>
              </Select>
              <Button
                colorScheme="blue"
                variant="outline"
                isLoading={userActivityLoading}
                onClick={fetchUserActivity}
                size="sm"
              >
                {userActivityLoaded ? 'Refresh' : 'Load Activity'}
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody>
          {userActivityError && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <AlertDescription>{userActivityError}</AlertDescription>
            </Alert>
          )}

          {!userActivityLoaded && !userActivityLoading && (
            <Text color="gray.400" fontSize="sm" textAlign="center" py={6}>
              Click "Load Activity" to fetch all users' DB operation history.
            </Text>
          )}

          {userActivityLoading && (
            <Center py={8}>
              <VStack>
                <Spinner />
                <Text fontSize="sm" color="gray.500">Loading activity data…</Text>
              </VStack>
            </Center>
          )}

          {userActivityLoaded && !userActivityLoading && userActivityData.length === 0 && (
            <Text color="gray.400" fontSize="sm" textAlign="center" py={6}>
              No activity data found.
            </Text>
          )}

          {userActivityLoaded && userActivityData.length > 0 && (
            <VStack spacing={3} align="stretch">
              {/* Legend */}
              <HStack spacing={4} fontSize="xs" color="gray.500" wrap="wrap">
                <HStack><Badge colorScheme="red" variant="solid">HIGH</Badge><Text>Heavy CPU + Disk I/O (storage uploads/downloads, large table scans)</Text></HStack>
                <HStack><Badge colorScheme="orange" variant="solid">MED</Badge><Text>Moderate load (page loads with joins, saves)</Text></HStack>
                <HStack><Badge colorScheme="green" variant="solid">LOW</Badge><Text>Minimal load (navigation, activity writes)</Text></HStack>
              </HStack>
              <HStack spacing={4} fontSize="xs" color="gray.500" wrap="wrap">
                <Text fontWeight="medium">Row color = total DB load:</Text>
                <HStack><Box w="12px" h="12px" bg="green.300" borderRadius="sm" /><Text>Low (&lt; 8 ops)</Text></HStack>
                <HStack><Box w="12px" h="12px" bg="orange.300" borderRadius="sm" /><Text>Medium (8–29 ops)</Text></HStack>
                <HStack><Box w="12px" h="12px" bg="red.300" borderRadius="sm" /><Text>High (≥ 30 ops)</Text></HStack>
              </HStack>

              {/* Per-user rows */}
              {userActivityData.map((user) => {
                const isExpanded = expandedUserIds.has(user.id);
                const hiCount = highImpactCount(user);
                const dayGroups = groupEventsByDay(user.events);

                // Compute total weighted DB load for this user
                const userImpacts = user.events.map(ev => classifyDbImpact(ev));
                const totalUserOps = userImpacts.reduce((sum, imp) => {
                  const actual = (imp.db_ops ?? 0) + (imp.storage_ops ?? 0);
                  if (actual > 0) return sum + actual;
                  // Heuristic fallback for events without cost metadata
                  if (imp.level === 'high') return sum + 4;
                  if (imp.level === 'medium') return sum + 1;
                  return sum;
                }, 0);

                // Significance level drives row color
                const userSig = totalUserOps >= 30 ? 'red'
                              : totalUserOps >= 8  ? 'orange'
                              : 'green';
                const rowBorderColor = userSig === 'red' ? 'red.300'
                                     : userSig === 'orange' ? 'orange.300'
                                     : 'green.200';
                const headerBg = userSig === 'red' ? 'red.50'
                               : userSig === 'orange' ? 'orange.50'
                               : 'green.50';
                const headerHoverBg = userSig === 'red' ? 'red.100'
                                    : userSig === 'orange' ? 'orange.100'
                                    : 'green.100';

                return (
                  <Box key={user.id} border="2px solid" borderColor={rowBorderColor} borderRadius="md" overflow="hidden">
                    {/* User header row */}
                    <Flex
                      px={4} py={3} bg={headerBg}
                      align="center" justify="space-between" cursor="pointer"
                      onClick={() => toggleUserExpand(user.id)}
                      _hover={{ bg: headerHoverBg }}
                    >
                      <HStack spacing={3} flex={1} minW={0}>
                        <Text fontWeight="semibold" fontSize="sm" isTruncated>
                          {user.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500" isTruncated>{user.email}</Text>
                        <Badge colorScheme="purple" variant="outline" fontSize="xs">{user.role}</Badge>
                      </HStack>
                      <HStack spacing={3} flexShrink={0}>
                        <Tooltip label="Total tracked events (up to 10 per action type × 50 action types)">
                          <Badge colorScheme="blue" variant="outline">{user.event_count} events</Badge>
                        </Tooltip>
                        <Tooltip label={`Estimated total DB + storage operations tracked. Green < 8, Orange 8–29, Red ≥ 30`}>
                          <Badge colorScheme={userSig === 'red' ? 'red' : userSig === 'orange' ? 'orange' : 'green'} variant="solid">
                            ~{totalUserOps} ops
                          </Badge>
                        </Tooltip>
                        {hiCount > 0 && (
                          <Tooltip label="Events classified as high DB load (CPU + Disk I/O intensive)">
                            <Badge colorScheme="red" variant="outline">{hiCount} high-impact</Badge>
                          </Tooltip>
                        )}
                        <Tooltip label="Copy all rows for this user">
                          <IconButton
                            icon={<span>📋</span>}
                            size="xs"
                            variant="ghost"
                            aria-label="Copy user rows"
                            onClick={(e) => { e.stopPropagation(); copyUserRows(user); }}
                          />
                        </Tooltip>
                        <Text fontSize="sm" color="gray.500">{isExpanded ? '▲' : '▼'}</Text>
                      </HStack>
                    </Flex>

                    {/* Expanded: day-grouped activity */}
                    <Collapse in={isExpanded}>
                      <Box px={4} py={3}>
                        {dayGroups.length === 0 && (
                          <Text fontSize="sm" color="gray.400">No timestamped events available.</Text>
                        )}
                        {dayGroups.map(([dateStr, events]) => {
                          const dayKey = `${user.id}-${dateStr}`;
                          const isDayOpen = expandedDays[dayKey] !== false; // default open
                          const dayImpacts = events.map(ev => classifyDbImpact(ev));
                          const dayHiCount = dayImpacts.filter(i => i.level === 'high').length;
                          const totalDbOps = dayImpacts.reduce((s, i) => s + (i.db_ops ?? 0), 0);
                          const totalStorageOps = dayImpacts.reduce((s, i) => s + (i.storage_ops ?? 0), 0);
                          const hasRealOps = dayImpacts.some(i => i.db_ops != null || i.storage_ops != null);

                          return (
                            <Box key={dateStr} mb={3} border="1px solid" borderColor="gray.100" borderRadius="md">
                              {/* Day header */}
                              <Flex
                                px={3} py={2}
                                bg={dayHiCount > 0 ? "orange.50" : "white"}
                                align="center" justify="space-between"
                                cursor="pointer"
                                onClick={() => toggleDayExpand(user.id, dateStr)}
                                _hover={{ bg: dayHiCount > 0 ? "orange.100" : "gray.50" }}
                                borderRadius="md"
                              >
                                <HStack spacing={3}>
                                  <Text fontSize="sm" fontWeight="medium">{formatDateLabel(dateStr)}</Text>
                                  <Badge colorScheme="gray" variant="outline" fontSize="xs">{events.length} events</Badge>
                                  {dayHiCount > 0 && <Badge colorScheme="red" fontSize="xs">{dayHiCount} high-impact</Badge>}
                                  {hasRealOps && totalDbOps > 0 && (
                                    <Tooltip label="Total DB queries tracked this day">
                                      <Badge colorScheme="blue" variant="outline" fontSize="xs">{totalDbOps} DB ops</Badge>
                                    </Tooltip>
                                  )}
                                  {hasRealOps && totalStorageOps > 0 && (
                                    <Tooltip label="Total storage uploads/downloads tracked this day">
                                      <Badge colorScheme="purple" variant="outline" fontSize="xs">{totalStorageOps} storage ops</Badge>
                                    </Tooltip>
                                  )}
                                </HStack>
                                <Text fontSize="xs" color="gray.400">{isDayOpen ? '▲' : '▼'}</Text>
                              </Flex>

                              {/* Day events table */}
                              <Collapse in={isDayOpen}>
                                <Box overflowX="auto">
                                  <Table size="sm" variant="simple">
                                    <Thead>
                                      <Tr bg="gray.50">
                                        <Th fontSize="xs" w="90px">Time</Th>
                                        <Th fontSize="xs" w="80px">Impact</Th>
                                        <Th fontSize="xs" w="90px">DB / Storage</Th>
                                        <Th fontSize="xs">Action</Th>
                                        <Th fontSize="xs">Category</Th>
                                        <Th fontSize="xs">Page / Details</Th>
                                        <Th fontSize="xs" w="160px">DB Load at Time</Th>
                                        <Th fontSize="xs">Why it causes load</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {events.map((ev, idx) => {
                                        const impact = classifyDbImpact(ev);
                                        const page =
                                          ev.metadata?.page ||
                                          ev.details?.page ||
                                          ev.details?.documentName ||
                                          ev.details?.patientName ||
                                          '—';
                                        const detailStr = [
                                          ev.details?.patientName && `Patient: ${ev.details.patientName}`,
                                          ev.details?.documentName && `Doc: ${ev.details.documentName}`,
                                          ev.details?.version_name && `Version: ${ev.details.version_name}`,
                                          ev.details?.trigger && `Trigger: ${ev.details.trigger}`,
                                          ev.details?.filePath && `Path: ${ev.details.filePath.split('/').pop()}`,
                                          !ev.details?.patientName && !ev.details?.documentName && !ev.details?.version_name && page !== '—' && `Page: ${page}`,
                                        ].filter(Boolean).join(' · ') || page;

                                        return (
                                          <Tr key={idx} _hover={{ bg: "gray.50" }}>
                                            <Td fontSize="xs" fontFamily="mono" color="gray.600">
                                              {formatTs(ev.timestamp)}
                                            </Td>
                                            <Td>
                                              <Tooltip label={impact.reason} placement="top" hasArrow maxW="320px">
                                                <Badge
                                                  colorScheme={impact.color}
                                                  variant={impact.level === 'high' ? 'solid' : 'subtle'}
                                                  cursor="help"
                                                  fontSize="xs"
                                                >
                                                  {impact.level.toUpperCase()}
                                                </Badge>
                                              </Tooltip>
                                            </Td>
                                            <Td fontSize="xs" color="gray.600">
                                              {impact.db_ops != null || impact.storage_ops != null ? (
                                                <HStack spacing={1}>
                                                  {impact.db_ops != null && (
                                                    <Tooltip label="DB queries">
                                                      <Badge colorScheme="blue" variant="outline" fontSize="xs">
                                                        {impact.db_ops}DB
                                                      </Badge>
                                                    </Tooltip>
                                                  )}
                                                  {impact.storage_ops != null && (
                                                    <Tooltip label="Storage uploads/downloads">
                                                      <Badge colorScheme="purple" variant="outline" fontSize="xs">
                                                        {impact.storage_ops}S
                                                      </Badge>
                                                    </Tooltip>
                                                  )}
                                                </HStack>
                                              ) : (
                                                <Text color="gray.300" fontSize="xs">—</Text>
                                              )}
                                            </Td>
                                            <Td fontSize="xs" fontWeight={impact.level === 'high' ? 'bold' : 'normal'}>
                                              {ev.action || '—'}
                                              {ev.total_count > 1 && (
                                                <Badge ml={1} colorScheme="gray" variant="outline" fontSize="xs">
                                                  ×{ev.total_count}
                                                </Badge>
                                              )}
                                            </Td>
                                            <Td fontSize="xs" color="gray.600">{ev.category || '—'}</Td>
                                            <Td fontSize="xs" color="gray.600" maxW="200px" isTruncated>
                                              <Tooltip label={detailStr}>
                                                <Text isTruncated>{detailStr}</Text>
                                              </Tooltip>
                                            </Td>
                                            <Td fontSize="xs" color="gray.600" w="160px">
                                              {(() => {
                                                const m = getNearestMetrics(ev.timestamp);
                                                if (!m) return <Text color="gray.300" fontSize="xs">no data yet</Text>;
                                                const cpuColor = m.cpu_percent >= 80 ? 'red' : m.cpu_percent >= 50 ? 'orange' : 'green';
                                                const cacheColor = m.cache_hit_ratio != null
                                                  ? (m.cache_hit_ratio < 0.8 ? 'red' : m.cache_hit_ratio < 0.95 ? 'orange' : 'green')
                                                  : 'gray';
                                                return (
                                                  <Tooltip label={`Sampled at ${formatTs(m.sampled_at)} · ${m.slow_queries_count ?? 0} slow quer${m.slow_queries_count === 1 ? 'y' : 'ies'}`} placement="top" hasArrow>
                                                    <HStack spacing={1} cursor="help" flexWrap="wrap">
                                                      {m.cpu_percent != null && (
                                                        <Badge colorScheme={cpuColor} variant="subtle" fontSize="xs">
                                                          CPU {m.cpu_percent.toFixed(0)}%
                                                        </Badge>
                                                      )}
                                                      {m.active_connections != null && (
                                                        <Badge colorScheme="blue" variant="outline" fontSize="xs">
                                                          {m.active_connections} conn
                                                        </Badge>
                                                      )}
                                                      {m.cache_hit_ratio != null && (
                                                        <Badge colorScheme={cacheColor} variant="subtle" fontSize="xs">
                                                          {(m.cache_hit_ratio * 100).toFixed(0)}% cache
                                                        </Badge>
                                                      )}
                                                    </HStack>
                                                  </Tooltip>
                                                );
                                              })()}
                                            </Td>
                                            <Td fontSize="xs" color="gray.500" maxW="260px">
                                              <Tooltip label={impact.reason} placement="top" hasArrow maxW="360px">
                                                <Text isTruncated cursor="help">{impact.reason}</Text>
                                              </Tooltip>
                                            </Td>
                                          </Tr>
                                        );
                                      })}
                                    </Tbody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </VStack>
          )}
        </CardBody>
      </Card>

        {/* Table Statistics */}
        <Card>
          <CardHeader>
            <Heading size="md">Table Statistics (Top 20)</Heading>
          </CardHeader>
          <CardBody>
            {data?.tables?.length > 0 ? (
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Table</Th>
                      <Th isNumeric>Seq Scans</Th>
                      <Th isNumeric>Idx Scans</Th>
                      <Th isNumeric>Inserts</Th>
                      <Th isNumeric>Updates</Th>
                      <Th isNumeric>Deletes</Th>
                      <Th isNumeric>Live Rows</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.tables.map((table, idx) => (
                      <Tr key={idx}>
                        <Td>
                          <Text fontSize="sm" fontWeight="medium">
                            {table.relname}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {table.schemaname}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">
                            {formatNumber(table.seq_scan)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">
                            {formatNumber(table.idx_scan)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" color="green.500">
                            {formatNumber(table.n_tup_ins)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" color="blue.500">
                            {formatNumber(table.n_tup_upd)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" color="red.500">
                            {formatNumber(table.n_tup_del)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">
                            {formatNumber(table.n_live_tup)}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text color="gray.500">No table statistics available.</Text>
            )}
          </CardBody>
        </Card>

        {/* CPU Analysis Summary */}
        {data?.cpu_analysis && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">CPU & Performance Analysis</Heading>
                <Badge
                  colorScheme={
                    data.cpu_analysis.slow_query_count > 0 ? "orange" : "green"
                  }
                  fontSize="md"
                >
                  {data.cpu_analysis.slow_query_count} slow queries
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
                <Stat>
                  <StatLabel>Slow Queries</StatLabel>
                  <StatNumber
                    color={
                      data.cpu_analysis.slow_query_count > 0
                        ? "orange"
                        : "green"
                    }
                  >
                    {data.cpu_analysis.slow_query_count}
                  </StatNumber>
                  <StatHelpText>Currently running</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Low Cache Tables</StatLabel>
                  <StatNumber
                    color={
                      data.cpu_analysis.low_cache_tables > 0
                        ? "orange"
                        : "green"
                    }
                  >
                    {data.cpu_analysis.low_cache_tables}
                  </StatNumber>
                  <StatHelpText>&lt; 90% hit ratio</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Unused Indexes</StatLabel>
                  <StatNumber
                    color={
                      data.cpu_analysis.unused_index_count > 0
                        ? "orange"
                        : "green"
                    }
                  >
                    {data.cpu_analysis.unused_index_count}
                  </StatNumber>
                  <StatHelpText>Never scanned</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Bloat Tables</StatLabel>
                  <StatNumber
                    color={data.cpu_analysis.bloat_tables > 0 ? "red" : "green"}
                  >
                    {data.cpu_analysis.bloat_tables}
                  </StatNumber>
                  <StatHelpText>Dead tuples</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Unindexed Tables</StatLabel>
                  <StatNumber
                    color={
                      data.cpu_analysis.unindexed_tables > 0
                        ? "orange"
                        : "green"
                    }
                  >
                    {data.cpu_analysis.unindexed_tables}
                  </StatNumber>
                  <StatHelpText>Seq scans only</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>DB Size</StatLabel>
                  <StatNumber fontSize="md">
                    {data.database_size?.database_size || "N/A"}
                  </StatNumber>
                  <StatHelpText>Total size</StatHelpText>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* Slow Queries with Resource Info */}
        {data?.slow_queries && data.slow_queries.length > 0 && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">Slow Running Queries</Heading>
                <Badge colorScheme="orange" fontSize="md">
                  {data.slow_queries.length} active
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>PID</Th>
                      <Th>User</Th>
                      <Th>State</Th>
                      <Th>Wait</Th>
                      <Th>IO Read</Th>
                      <Th>Temp MB</Th>
                      <Th>Duration</Th>
                      <Th>Query</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.slow_queries.map((query, idx) => {
                      const tempMB =
                        ((query.temp_bytes || query.temp_blks_read || 0) *
                          8.192) /
                        1024;
                      const ioRead =
                        ((query.blocks_read || query.shared_blks_read || 0) *
                          8.192) /
                        1024;
                      return (
                        <Tr key={idx}>
                          <Td>
                            <Code fontSize="xs">{query.pid}</Code>
                          </Td>
                          <Td>
                            <Text fontSize="sm">{query.usename}</Text>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStateColor(query.state)}>
                              {query.state}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={
                                query.wait_event_type === "Lock"
                                  ? "red"
                                  : "gray"
                              }
                              size="sm"
                            >
                              {query.wait_event_type || "CPU"}
                            </Badge>
                          </Td>
                          <Td>
                            <Text
                              fontSize="sm"
                              color={ioRead > 10 ? "orange.500" : "gray.500"}
                            >
                              {ioRead < 0.01 ? "< 0.01" : ioRead.toFixed(2)} MB
                            </Text>
                          </Td>
                          <Td>
                            <Text
                              fontSize="sm"
                              color={tempMB > 1 ? "purple.500" : "gray.500"}
                            >
                              {tempMB < 0.001 ? "< 0.001" : tempMB.toFixed(3)}{" "}
                              MB
                            </Text>
                          </Td>
                          <Td>
                            <Text
                              fontSize="sm"
                              color="orange.500"
                              fontWeight="bold"
                            >
                              {query.duration_seconds}s
                            </Text>
                          </Td>
                          <Td maxW="300px">
                            <Code
                              fontSize="xs"
                              display="block"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {query.query?.substring(0, 100) || "-"}
                            </Code>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Table Access Analysis */}
        {data?.table_access && data.table_access.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Table Access Analysis (Heavy Tables)</Heading>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Table</Th>
                      <Th isNumeric>Seq Scans</Th>
                      <Th isNumeric>Idx Scans</Th>
                      <Th isNumeric>Heap Reads</Th>
                      <Th isNumeric>Heap Hits</Th>
                      <Th isNumeric>Idx Reads</Th>
                      <Th isNumeric>Idx Hits</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.table_access.slice(0, 15).map((table, idx) => (
                      <Tr key={idx}>
                        <Td>
                          <Text fontSize="sm" fontWeight="medium">
                            {table.relname}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {table.schemaname}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" color="orange.500">
                            {formatNumber(table.seq_scan)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">
                            {formatNumber(table.idx_scan)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">
                            {formatNumber(table.heap_blks_read)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" color="green.500">
                            {formatNumber(table.heap_blks_hit)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">
                            {formatNumber(table.idx_blks_read)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" color="green.500">
                            {formatNumber(table.idx_blks_hit)}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Cache Hit Ratio */}
        {data?.cache_hit_ratio && data.cache_hit_ratio.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">
                Cache Hit Ratio (Tables Needing Attention)
              </Heading>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Table</Th>
                      <Th isNumeric>Heap Hit %</Th>
                      <Th isNumeric>Idx Hit %</Th>
                      <Th isNumeric>Total Hit %</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.cache_hit_ratio.slice(0, 15).map((table, idx) => (
                      <Tr key={idx}>
                        <Td>
                          <Text fontSize="sm" fontWeight="medium">
                            {table.relname}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text
                            fontSize="sm"
                            color={
                              table.heap_hit_ratio < 90 ? "orange" : "green"
                            }
                          >
                            {table.heap_hit_ratio?.toFixed(2)}%
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text
                            fontSize="sm"
                            color={
                              table.idx_hit_ratio < 90 ? "orange" : "green"
                            }
                          >
                            {table.idx_hit_ratio?.toFixed(2)}%
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            color={table.total_hit_ratio < 90 ? "red" : "green"}
                          >
                            {table.total_hit_ratio?.toFixed(2)}%
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Index Usage Analysis */}
        {data?.index_usage && data.index_usage.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Index Usage (Potential Issues)</Heading>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Table</Th>
                      <Th>Index</Th>
                      <Th isNumeric>Scans</Th>
                      <Th isNumeric>Size</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.index_usage.slice(0, 15).map((idx, i) => (
                      <Tr key={i}>
                        <Td>
                          <Text fontSize="sm">{idx.relname}</Text>
                        </Td>
                        <Td>
                          <Code fontSize="xs">{idx.indexrelname}</Code>
                        </Td>
                        <Td isNumeric>
                          <Text
                            fontSize="sm"
                            color={idx.idx_scan === 0 ? "red" : "orange"}
                          >
                            {formatNumber(idx.idx_scan)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">{idx.index_size}</Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Vacuum/ Bloat Analysis */}
        {data?.vacuum_stats && data.vacuum_stats.length > 0 && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">Table Bloat (Vacuum Needed)</Heading>
                <Badge colorScheme="red" fontSize="md">
                  {data.vacuum_stats.length} tables
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Table</Th>
                      <Th isNumeric>Dead Tuples</Th>
                      <Th isNumeric>Live Tuples</Th>
                      <Th isNumeric>Dead %</Th>
                      <Th isNumeric>Wasted Space</Th>
                      <Th>Last Vacuum</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.vacuum_stats.slice(0, 10).map((table, idx) => (
                      <Tr key={idx}>
                        <Td>
                          <Text fontSize="sm" fontWeight="medium">
                            {table.relname}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" color="red.500">
                            {formatNumber(table.n_dead_tup)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">
                            {formatNumber(table.n_live_tup)}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" color="orange.500">
                            {table.dead_tuple_percent?.toFixed(1)}%
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Tooltip
                            label={
                              table.estimated_wasted_bytes
                                ? `${table.estimated_wasted_bytes} bytes`
                                : "N/A"
                            }
                          >
                            <Text
                              fontSize="sm"
                              color={
                                table.estimated_wasted_mb > 0.1
                                  ? "purple.500"
                                  : "gray.500"
                              }
                              fontWeight={
                                table.estimated_wasted_mb > 0.1
                                  ? "medium"
                                  : "normal"
                              }
                            >
                              {table.estimated_wasted_mb
                                ? `${table.estimated_wasted_mb.toFixed(3)} MB`
                                : table.estimated_wasted_bytes
                                  ? formatBytes(table.estimated_wasted_bytes)
                                  : "0 B"}
                            </Text>
                          </Tooltip>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color="gray.500">
                            {table.last_autovacuum
                              ? new Date(
                                  table.last_autovacuum,
                                ).toLocaleDateString()
                              : "Never"}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* CPU Wait Statistics */}
        {data?.cpu_wait_stats && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">CPU Wait Events</Heading>
                <Badge
                  colorScheme={
                    data.cpu_wait_stats.length > 0 ? "orange" : "green"
                  }
                >
                  {data.cpu_wait_stats.length || 0} events
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              {data.cpu_wait_stats.length > 0 ? (
                <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                  {data.cpu_wait_stats.map((stat, idx) => (
                    <Stat key={idx}>
                      <StatLabel>
                        {stat.wait_event || stat.wait_event_type || "CPU"}
                      </StatLabel>
                      <StatNumber color={stat.count > 5 ? "orange" : "green"}>
                        {stat.count}
                      </StatNumber>
                      <StatHelpText>
                        {stat.wait_event_type || "active"}
                      </StatHelpText>
                    </Stat>
                  ))}
                </SimpleGrid>
              ) : (
                <Text color="green.500">
                  No active CPU wait events - database is idle
                </Text>
              )}
            </CardBody>
          </Card>
        )}

        {/* Function Data Usage Monitor */}
        <FunctionDataMonitor />

        {/* Disk IO Bandwidth Monitor */}
        <DiskIOMonitor />

        {/* Last Updated */}
        <Box textAlign="center" py={2}>
          <Text fontSize="sm" color="gray.500">
            Last updated: {formatDate(lastUpdated)}
          </Text>
        </Box>
      </VStack>

      {/* Alert Configuration Modal */}
      <Modal isOpen={isAlertConfigOpen} onClose={closeAlertConfig} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>⚙️ Alert Configuration</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="bold" mb={3}>
                  CPU Thresholds
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">Warning Threshold (%)</FormLabel>
                  <Slider
                    value={alertThresholds.cpuWarning}
                    onChange={(val) =>
                      setAlertThresholds({
                        ...alertThresholds,
                        cpuWarning: val,
                      })
                    }
                    min={30}
                    max={90}
                    step={5}
                  >
                    <SliderTrack>
                      <SliderFilledTrack bg="orange.400" />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text fontSize="xs" mt={1}>
                    Current: {alertThresholds.cpuWarning}%
                  </Text>
                </FormControl>
                <FormControl mt={3}>
                  <FormLabel fontSize="sm">Critical Threshold (%)</FormLabel>
                  <Slider
                    value={alertThresholds.cpuCritical}
                    onChange={(val) =>
                      setAlertThresholds({
                        ...alertThresholds,
                        cpuCritical: val,
                      })
                    }
                    min={50}
                    max={100}
                    step={5}
                  >
                    <SliderTrack>
                      <SliderFilledTrack bg="red.400" />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text fontSize="xs" mt={1}>
                    Current: {alertThresholds.cpuCritical}%
                  </Text>
                </FormControl>
              </Box>

              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="bold" mb={3}>
                  Query Performance
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">
                    Slow Query Warning (seconds)
                  </FormLabel>
                  <NumberInput
                    value={alertThresholds.slowQueryWarning}
                    onChange={(_, val) =>
                      setAlertThresholds({
                        ...alertThresholds,
                        slowQueryWarning: val || 5,
                      })
                    }
                    min={1}
                    max={60}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl mt={3}>
                  <FormLabel fontSize="sm">
                    Slow Query Critical (seconds)
                  </FormLabel>
                  <NumberInput
                    value={alertThresholds.slowQueryCritical}
                    onChange={(_, val) =>
                      setAlertThresholds({
                        ...alertThresholds,
                        slowQueryCritical: val || 30,
                      })
                    }
                    min={10}
                    max={300}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </Box>

              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="bold" mb={3}>
                  Connection Limits
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">Warning (connections)</FormLabel>
                  <NumberInput
                    value={alertThresholds.connectionWarning}
                    onChange={(_, val) =>
                      setAlertThresholds({
                        ...alertThresholds,
                        connectionWarning: val || 80,
                      })
                    }
                    min={10}
                    max={180}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl mt={3}>
                  <FormLabel fontSize="sm">Maximum (connections)</FormLabel>
                  <NumberInput
                    value={alertThresholds.maxConnections}
                    onChange={(_, val) =>
                      setAlertThresholds({
                        ...alertThresholds,
                        maxConnections: val || 150,
                      })
                    }
                    min={50}
                    max={200}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </Box>

              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="bold" mb={3}>
                  Alert Settings
                </Text>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb={0} fontSize="sm">
                    Enable Alerts
                  </FormLabel>
                  <Switch
                    isChecked={alertThresholds.enableAlerts}
                    onChange={(e) =>
                      setAlertThresholds({
                        ...alertThresholds,
                        enableAlerts: e.target.checked,
                      })
                    }
                    colorScheme="green"
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center" mt={2}>
                  <FormLabel mb={0} fontSize="sm">
                    Auto-notify (email)
                  </FormLabel>
                  <Switch
                    isChecked={alertThresholds.enableAutoNotify}
                    onChange={(e) =>
                      setAlertThresholds({
                        ...alertThresholds,
                        enableAutoNotify: e.target.checked,
                      })
                    }
                    colorScheme="green"
                  />
                </FormControl>
                {alertThresholds.enableAutoNotify && (
                  <FormControl mt={2}>
                    <Input
                      placeholder="Enter email for notifications"
                      value={alertThresholds.alertEmail}
                      onChange={(e) =>
                        setAlertThresholds({
                          ...alertThresholds,
                          alertEmail: e.target.value,
                        })
                      }
                      size="sm"
                    />
                  </FormControl>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={closeAlertConfig}>
              Save & Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


      {/* Process Control Modal */}
      <Modal
        isOpen={isProcessControlOpen}
        onClose={closeProcessControl}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>🔧 Process Control</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedProcess && (
              <VStack spacing={4} align="stretch">
                <Box p={3} bg="red.50" borderRadius="md">
                  <Text fontWeight="bold" color="red.600">
                    Process Information
                  </Text>
                  <Text fontSize="sm" mt={2}>
                    <strong>PID:</strong> {selectedProcess.pid}
                  </Text>
                  <Text fontSize="sm">
                    <strong>User:</strong> {selectedProcess.usename}
                  </Text>
                  <Text fontSize="sm">
                    <strong>State:</strong> {selectedProcess.state}
                  </Text>
                  <Text fontSize="sm">
                    <strong>Duration:</strong>{" "}
                    {selectedProcess.query_start
                      ? `${Math.round((new Date() - new Date(selectedProcess.query_start)) / 1000)}s`
                      : "N/A"}
                  </Text>
                  <Box mt={2}>
                    <Text fontSize="sm" fontWeight="medium">
                      Query:
                    </Text>
                    <Code
                      fontSize="xs"
                      display="block"
                      p={2}
                      bg="white"
                      borderRadius="sm"
                    >
                      {selectedProcess.query?.substring(0, 200) || "-"}
                    </Code>
                  </Box>
                </Box>

                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Set Priority
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      colorScheme="green"
                      variant={processPriority === "high" ? "solid" : "outline"}
                      onClick={() => setProcessPriority("high")}
                    >
                      ↑ High
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="gray"
                      variant={
                        processPriority === "normal" ? "solid" : "outline"
                      }
                      onClick={() => setProcessPriority("normal")}
                    >
                      Normal
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant={processPriority === "low" ? "solid" : "outline"}
                      onClick={() => setProcessPriority("low")}
                    >
                      ↓ Low
                    </Button>
                  </HStack>
                </Box>

                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Warning</AlertTitle>
                    <AlertDescription fontSize="xs">
                      Terminating a process may cause data loss or
                      inconsistencies. Use with caution.
                    </AlertDescription>
                  </Box>
                </Alert>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="outline"
                onClick={() =>
                  handleSetPriority(selectedProcess?.pid, processPriority)
                }
              >
                Apply Priority
              </Button>
              <Button
                colorScheme="red"
                isLoading={isTerminating}
                onClick={() => handleTerminateProcess(selectedProcess?.pid)}
              >
                Terminate Process
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Query Detail Modal */}
      <Modal
        isOpen={isQueryDetailOpen}
        onClose={handleQueryDetailClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>📝 Query Details</ModalHeader>
          <ModalCloseButton onClick={handleQueryDetailClose} />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <HStack>
                <Text fontWeight="bold">PID:</Text>
                <Text>{selectedQuery?.pid}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">User:</Text>
                <Text>{selectedQuery?.usename}</Text>
              </HStack>
              <HStack>
                <Text fontWeight="bold">State:</Text>
                <Badge colorScheme={getStateColor(selectedQuery?.state)}>
                  {selectedQuery?.state}
                </Badge>
              </HStack>
              <HStack>
                <Text fontWeight="bold">Duration:</Text>
                <Text>
                  {selectedQuery?.query_start
                    ? `${Math.round((new Date() - new Date(selectedQuery.query_start)) / 1000)}s`
                    : "-"}
                </Text>
              </HStack>
              <Box>
                <Text fontWeight="bold" mb={2}>
                  Query:
                </Text>
                <Code
                  display="block"
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  maxH="300px"
                  overflowY="auto"
                >
                  {selectedQuery?.query || "-"}
                </Code>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                colorScheme={copiedQuery ? "green" : "blue"}
                onClick={() => {
                  navigator.clipboard.writeText(selectedQuery?.query || "");
                  setCopiedQuery(true);
                  toast({
                    title: "Query copied to clipboard!",
                    status: "success",
                    duration: 2000,
                  });
                  setTimeout(() => setCopiedQuery(false), 2000);
                }}
              >
                {copiedQuery ? "✓ Copied!" : "Copy Query"}
              </Button>
              <Button onClick={handleQueryDetailClose}>Close</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default MonitorPage;
